import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('adforge_token'));

  const fetchUsage = useCallback(async (authToken) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.get(`${API}/usage`, {
        withCredentials: true,
        headers
      });
      setUsage(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers
      });
      setUser(response.data);
      // Fetch usage after getting user
      await fetchUsage(token);
    } catch (error) {
      setUser(null);
      setUsage(null);
      setToken(null);
      localStorage.removeItem('adforge_token');
    } finally {
      setLoading(false);
    }
  }, [token, fetchUsage]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    localStorage.setItem('adforge_token', access_token);
    setUser(userData);
    await fetchUsage(access_token);
    return userData;
  };

  const register = async (email, password, name, extras = {}) => {
    const response = await axios.post(`${API}/auth/register`, {
      email, password, name,
      marketing_consent: !!extras.marketing_consent,
      ai_terms_accepted: !!extras.ai_terms_accepted,
      language: extras.language || 'en'
    });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    localStorage.setItem('adforge_token', access_token);
    setUser(userData);
    await fetchUsage(access_token);
    return userData;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleCallback = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/google/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    // Fetch usage for Google auth users
    await fetchUsage(null);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setUsage(null);
    setToken(null);
    localStorage.removeItem('adforge_token');
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const refreshUsage = async () => {
    await fetchUsage(token);
  };

  const value = {
    user,
    usage,
    loading,
    token,
    login,
    register,
    googleLogin,
    processGoogleCallback,
    logout,
    refreshUser,
    refreshUsage,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    canGenerate: usage?.can_generate ?? true
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
